import isEqual from 'lodash/isEqual';
import createAction from '@framework/state/createAction';
import isEmptyString from '@framework/data-helpers/string/isEmptyString';
import Logger from '@framework/sentry/Logger';
import { removeQueryParams, addQueryParams } from '@framework/router/nextRouter';
import { getContainer } from '@framework/di/getContainer';
import TraceRepository from '@/creditflowresearch/infrastructure/repository/TraceRepository';
import { TRACE_STORE_NAME } from '@/creditflowresearch/application/Trace/constants';
import { ITraceFilterResponse } from '@/condor/domain/vo/types';
import actionTypes from './actionTypes';
import { IReactReducerDispatch } from '@/creditflowresearch/infrastructure/state/createAsyncRequest';
import { TTraceFiltersPayload } from '@/creditflowresearch/domain/vo/Trace/IMarketTradesPayload';
import { ITraceFiltersState } from './reducer';
import {
  getFiltersPayload as buildFiltersPayload,
  getInitialState,
  IInitialState,
  mapHashPayloadToFilters,
  mapRangesForFilters,
} from './helpers';

type TFilters = object;
type TCheckboxes = Record<string, unknown>;
type TFiltersUpdate = (filterName: string, filters: TFilters) => void;

interface ITraceFilterResult {
  filter: Partial<ITraceFilterResponse['filter']>,
  hash?: string,
}

interface IRouter {
  query: Record<string, string | string[]>,
  push: (url: { query: Record<string, string | string[]> }, as: undefined, options: { shallow: boolean }) => unknown,
}

interface IProps {
  dispatch: IReactReducerDispatch,
  getState: () => ITraceFiltersState,
}

export default ({ dispatch, getState }: IProps) => {
  const traceRepository = getContainer().get(TraceRepository);

  const payloadChange = (payload: TTraceFiltersPayload) => dispatch(
    createAction(actionTypes.TRACE_FILTER_PAYLOAD, payload),
  );

  const initialFiltersChange = (payload: TFilters) => dispatch(createAction(actionTypes.TRACE_INITIAL_FILTERS, payload));

  const appliedFiltersChange = (payload: TFilters) => dispatch(createAction(actionTypes.TRACE_FILTER_APPLY, payload));

  const getFiltersPayload = (filters: TFilters = {}, checkboxes: TCheckboxes): TTraceFiltersPayload => (
    buildFiltersPayload(filters, checkboxes, getState().items?.filter || {})
  );

  const getTraceFilter = async (filtersPayload: TTraceFiltersPayload): Promise<ITraceFilterResult> => {
    dispatch(createAction(actionTypes.GET_TRACE_FILTER));
    try {
      const response = await traceRepository.getTraceFilter({ ...filtersPayload });

      dispatch(createAction(actionTypes.GET_TRACE_FILTER_SUCCESS, response));
      return response;
    } catch (err) {
      dispatch(createAction(actionTypes.GET_TRACE_FILTER_ERROR));
      return {
        filter: {},
      };
    }
  };

  const setMinMaxDatePeriod = (dateFrom: number, dateTo: number) => {
    dispatch(createAction(actionTypes.TRACE_FILTER_MIN_DATE_FROM, dateFrom));
    dispatch(createAction(actionTypes.TRACE_FILTER_MAX_DATE_TO, dateTo));
  };

  const applyFilter = (filters: TFilters, checkboxes: TCheckboxes, router?: IRouter) => {
    const { initialFilters } = getState();
    const newFiltersPayload = getFiltersPayload(filters, checkboxes);

    const isInitialFilters = isEqual(initialFilters, filters);
    const appliedFilters = isInitialFilters ? initialFilters : filters;
    payloadChange(newFiltersPayload);
    appliedFiltersChange(appliedFilters);

    if (isInitialFilters && router) {
      const currentQuery = removeQueryParams(router.query, ['dateFrom', 'dateTo', 'hash']);
      router.push({
        query: currentQuery,
      }, undefined, { shallow: true });
    }
  };

  const fetchFilter = async (
    includedAssetClassId: string,
    filters: TFilters,
    checkboxes: TCheckboxes,
    router?: IRouter,
  ) => {
    const { initialFilters } = getState();
    const newFiltersPayload = getFiltersPayload(filters, checkboxes);

    const response = await getTraceFilter({ includedAssetClassId, ...newFiltersPayload });
    const traceFilter = response.filter || {};
    const isTraceFilter = !!Object.keys(traceFilter).length;
    const hash = response.hash || '';
    const isInitialFilters = isEqual(initialFilters, filters);
    const isHash = isEmptyString(hash);

    if (isTraceFilter) {
      const { tradeDate } = traceFilter;
      setMinMaxDatePeriod(tradeDate.range.min, tradeDate.range.max);
    }
    if (!isHash && !isInitialFilters && router) {
      router.push({
        query: addQueryParams(router.query, { hash }),
      }, undefined, { shallow: true });
    }
  };

  const initFilters = async (
    filtersPayload: TTraceFiltersPayload,
    filtersState: IInitialState = getInitialState(),
    isHashPayload: boolean = false,
    includedAssetClassId: string = null,
    filtersUpdate: TFiltersUpdate,
  ) => {
    try {
      const response = await getTraceFilter({ includedAssetClassId, ...filtersPayload });
      const traceFilter = response.filter;

      const newFiltersState = Object.assign(filtersState, mapRangesForFilters(traceFilter));

      const initialFilters = Object.assign(getInitialState(), mapRangesForFilters(traceFilter));
      initialFiltersChange(initialFilters);

      const filters = isHashPayload
        // @ts-ignore TODO: ITraceFilterResponse types sectorList as ISector ({ id }), the helper reads { sectorId }
        ? mapHashPayloadToFilters(filtersPayload, traceFilter)
        : newFiltersState;
      filtersUpdate(TRACE_STORE_NAME, filters);
      appliedFiltersChange(filters);

      setMinMaxDatePeriod(traceFilter.tradeDate.range.min, traceFilter.tradeDate.range.max);
    } catch (error) {
      Logger.warn({ message: 'Failed to set min/max date period for trace', error });
    }
  };

  const resetFilters = async (
    includedAssetClassId: string,
    filtersUpdate: TFiltersUpdate,
    checkboxes: TCheckboxes,
    router?: IRouter,
  ) => {
    const initialState = getInitialState();
    // Pass filters through all components
    const newFiltersPayload = getFiltersPayload(getInitialState(), checkboxes);

    payloadChange(newFiltersPayload);
    await initFilters(newFiltersPayload, initialState, false, includedAssetClassId, filtersUpdate);

    if (router) {
      const currentQuery = removeQueryParams(router.query, ['dateFrom', 'dateTo', 'hash']);
      router.push({
        query: currentQuery,
      }, undefined, { shallow: true });
    }
  };

  return {
    payloadChange,
    getFiltersPayload,
    getTraceFilter,
    setMinMaxDatePeriod,
    applyFilter,
    fetchFilter,
    initFilters,
    resetFilters,
  };
};
